import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../lib/auth.js'

export interface TemplateItem {
  readonly id: string
  readonly name: string
  readonly title: string
  readonly description: string
  readonly category: string
  readonly priority: 'HIGH' | 'MEDIUM' | 'LOW'
  readonly complexity: number
  readonly cadence: 'DAILY' | 'WEEKLY' | 'MONTHLY' | null
  readonly weekday: number | null
  readonly dayOfMonth: number | null
  readonly dueTime: string | null
  readonly isPaused: boolean
  readonly defaultAssignee: { readonly id: string; readonly name: string } | null
  readonly assignee: { readonly id: string; readonly name: string; readonly isActive: boolean } | null
}

export interface TemplatePayload {
  readonly name: string
  readonly title: string
  readonly description: string
  readonly category: string
  readonly priority: string
  readonly complexity: number
  readonly defaultAssigneeId?: string | null
  readonly cadence?: string | null
  readonly weekday?: number | null
  readonly dayOfMonth?: number | null
  readonly dueTime?: string | null
  readonly assigneeId?: string | null
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

/** Plain-language cadence, per PRD §12.2 - no cron strings in front of a manager. */
export function cadenceSummary(template: TemplateItem): string {
  if (template.cadence === null) return 'Manual'
  const at = template.dueTime === null ? '' : ` at ${template.dueTime}`
  if (template.cadence === 'DAILY') return `Every day${at}`
  if (template.cadence === 'WEEKLY') {
    const day = template.weekday === null ? '' : ` ${WEEKDAYS[template.weekday - 1] ?? ''}`
    return `Every${day}${at}`
  }
  const day = template.dayOfMonth === null ? '' : ` day ${template.dayOfMonth}`
  return `Monthly on${day}${at}`
}

export function useTemplates(enabled = true) {
  const { api } = useAuth()
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<TemplateItem[]>('/api/templates'),
    enabled,
  })
}

export function useCreateTemplate() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: TemplatePayload) => api.post<TemplateItem>('/api/templates', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function usePatchTemplate() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; patch: Partial<TemplatePayload> & { isPaused?: boolean } }) =>
      api.patch<TemplateItem>(`/api/templates/${encodeURIComponent(input.id)}`, input.patch),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

export function useDeleteTemplate() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: boolean }>(`/api/templates/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}
